<script>
import { mapGetters, mapState } from 'vuex'
import BreadCrumbs from '@components/breadcrumbs/breadcrumbs-wrapper.vue'
import { dateTimeFormatters } from '@src/utils/date-formatters'
const { formatDateUnix } = dateTimeFormatters()
export default {
  data () {
    return {}
  },
  props: {
    hideRouterView: {
      type: Boolean,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: true
    }
  },
  components: {
    BreadCrumbs
  },
  computed: {
    ...mapState(['user', 'home', 'settings', 'challenges', 'challengePage']),
    ...mapGetters(['primaryColor', 'loggedIn']),
    charges () {
      return this.user && this.user.customer && this.user.customer.charges
    }
  },
  methods: {
    getPaymentDescription (charge) {
      if (
        charge &&
        charge.invoice &&
        charge.invoice.lines &&
        charge.invoice.lines.data &&
        charge.invoice.lines.data.length > 0 &&
        charge.invoice.lines.data[0].description
      ) {
        return charge.invoice.lines.data[0].description
      } else {
        return charge.description
      }
    },
    getDate (date) {
      if (date) {
        return formatDateUnix(date, 'LL')
      }
    },
    capitalizeFirstLetter (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
    getPaymentMethod (paymentMethodDetails) {
      if (paymentMethodDetails.type === 'card') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.card.brand) +
          ' ' +
          paymentMethodDetails.card.last4
        )
      }
      if (paymentMethodDetails.type === 'ach_credit_transfer') {
        return this.capitalizeFirstLetter(
          paymentMethodDetails.ach_credit_transfer.bank_name
        )
      }
      if (paymentMethodDetails.type === 'ach_debit') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.ach_debit.bank_name) +
          ' ' +
          paymentMethodDetails.ach_debit.last4
        )
      }
      if (paymentMethodDetails.type === 'au_becs_debit') {
        return (
          this.capitalizeFirstLetter(
            paymentMethodDetails.au_becs_debit.bsb_number
          ) +
          ' ' +
          paymentMethodDetails.au_becs_debit.last4
        )
      }
      if (paymentMethodDetails.type === 'bacs_debit') {
        return (
          this.capitalizeFirstLetter(
            paymentMethodDetails.bacs_debit.sort_code
          ) +
          ' ' +
          paymentMethodDetails.bacs_debit.last4
        )
      }
      if (paymentMethodDetails.type === 'card_present') {
        return (
          this.capitalizeFirstLetter(paymentMethodDetails.card_present.brand) +
          ' ' +
          paymentMethodDetails.card_present.last4
        )
      }
    },
    viewInvoice (url) {
      window.open(url, '_blank')
    }
  }
}
</script>

<template>
  <div class="payment-block-container">
     <BreadCrumbs class="second" v-if="!hideRouterView && isMobile" @back="$emit('hide-router-view')" />
    <div class="payment-header col">
      <div class="row">
        <div class="col">
          <label>{{ this.$_polyglot.t('profile.purchases.amounttitle') }}</label>
        </div>
        <div class="col">
          <label>{{ this.$_polyglot.t('profile.purchases.subscriptiontitle') }}</label>
        </div>
        <div class="col date">
          <label>{{ this.$_polyglot.t('profile.purchases.datetitle') }}</label>
        </div>
        <div class="col payment">
          <label>{{ this.$_polyglot.t('profile.purchases.paymentmethodtitle') }}</label>
        </div>
      </div>
    </div>
    <div v-if="charges && charges.length > 0" class="col payment-list">
      <template v-for="charge in charges">
        <div
          :key="charge.id"
          class="summary-block col"
          @click="viewInvoice(charge.receipt_url)"
        >
          <div class="row">
            <div class="col">
              <span class="detail"
                >{{
                  charge.refunded
                    ? `($${charge.amount_refunded / 100})`
                    : `$${charge.amount / 100}`
                }}
              </span>
            </div>
            <div class="col">
              <span class="sub-name"
                >{{ charge.refunded ? 'Refund - ' : ''
                }}{{ getPaymentDescription(charge) }}</span
              >
            </div>
            <div class="col">
              <span class="detail">{{ getDate(charge.created) }}</span>
            </div>
            <div class="col">
              <span class="detail payment">{{
                getPaymentMethod(charge.payment_method_details)
              }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div v-else>
      <div class="no-transactions col">
        <div class="row">
          <div class="col">
            <span class="detail">{{ this.$_polyglot.t('profile.purchases.nopayment') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.payment-block-container {
  width: 100%;
  padding: 3rem 4.5%;
  .breadcrumb-wrapper {
    margin-bottom: 0.1rem;
    align-self: flex-start;
    margin-left: -0.5rem;
    z-index: 3;
    &.second {
      position: absolute;
      left: 0.5rem;
      top: 0.5rem;
    }
    ::v-deep span {
      img {
        margin-right: 0.2rem;
      }
      h5 {
        font-size: 1.2rem;
      }
    }
  }
}

.payment-header {
  margin-top: 1rem;
  align-items: center;
  background: rgba(#c4c2bc, 0.33);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: center;
  height: 3rem;
  margin-bottom: 2.2rem;
  mix-blend-mode: normal;
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: nowrap;
    .col {
      display: inline-block;
      width: 26%;
      &:not(:first-child) {
        padding-left: unset;
        padding-right: 1.5rem;
      }
      &.payment {
        margin-right: unset;
      }
    }
    label {
      margin-bottom: unset;
      font-size: 0.8rem;
      white-space: nowrap;
      /* Fallback for non-webkit */
      display: -webkit-box;
      font-size: 12px;
      height: 100%;
      letter-spacing: -0.5px;

      /* Fallback for non-webkit */
      -webkit-line-clamp: 1;
      line-height: 12px;
      margin-bottom: unset;
      max-width: 100%;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }
}

.summary-block {
  align-items: center;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: center;
  cursor: pointer;
  display: flex;
  margin-bottom: 1rem;
  transition: transform 300ms;
  transition-timing-function: ease-out;
  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba(black, 0.1);
    transform: scale(1.01);
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    .col {
      align-items: center;
      display: flex;
      &:not(:first-child) {
        padding-left: unset;
        padding-right: 1.6rem;
      }
    }
    .detail {
      font-size: 15px;
      font-weight: normal;
      line-height: 21px;
      margin-bottom: unset;
      &.payment {
        margin-left: 0.6rem;
      }
    }
    .sub-name {
      -webkit-box-orient: vertical;
      display: block;

      /* Fallback for non-webkit */
      display: -webkit-box;
      font-size: 15px;
      font-style: normal;
      font-weight: normal;
      height: auto;
      letter-spacing: -0.3px;

      /* Fallback for non-webkit */
      -webkit-line-clamp: 2;
      line-height: 21px;
      margin-bottom: unset;
      max-width: 100%;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      word-break: break-all;
    }
  }
}

.no-transactions {
  align-items: center;
  background: rgba(#c4c2bc, 0.33);
  border-radius: 12px;
  display: flex;
  height: 4rem;
  margin-bottom: 1rem;
  position: relative;
  .row {
    margin-left: 2rem;
    width: 85%;
    .col {
      align-items: center;
      display: flex;
    }
    .detail {
      font-size: 18px;
      font-weight: normal;
      line-height: 21px;
      margin-bottom: unset;
    }
  }
}
</style>
